/**
 * The link contract between the dashboard's "Needs attention" list and the
 * pages it points at.
 *
 * Each item's "Review" used to be a plain `<Link to="/zones">`: it dropped you
 * on the full list with nothing selected and no hint which zone or sensor the
 * item meant, so the click answered nothing and the user had to re-find the
 * problem by hand. An item now carries the row it is about, and where an actual
 * review flow exists (Rachio drift, a calibration suggestion) the link opens
 * that flow directly.
 *
 * The URL is the carrier rather than router state so the destination survives a
 * refresh and can be linked to from anywhere. It is consumed once on arrival
 * (see `useAttentionTarget`) and then stripped.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Pages that can be deep-linked into. */
export type AttentionPage = 'zones' | 'sensors';

/** Review flows a link can open on arrival. Anything else just reveals a row. */
export type ReviewFlow = 'drift' | 'calibration' | 'flow';

export type AttentionTarget = {
  page: AttentionPage;
  /** Zone id or sensor id, depending on `page`. */
  id: string;
  review?: ReviewFlow;
};

/** Query key naming the row, per page. */
const ID_PARAM: Record<AttentionPage, string> = { zones: 'zone', sensors: 'sensor' };

const REVIEW_FLOWS: ReviewFlow[] = ['drift', 'calibration', 'flow'];

/** `/zones?zone=abc&review=drift` */
export function attentionHref(target: AttentionTarget): string {
  const query = new URLSearchParams({ [ID_PARAM[target.page]]: target.id });
  if (target.review) query.set('review', target.review);
  return `/${target.page}?${query.toString()}`;
}

/**
 * The target a URL's query names, if any. Tolerant by design: a stale or
 * hand-edited link with an unknown `review` still reveals the row rather than
 * failing, and an unknown row id is the caller's problem to shrug off.
 */
export function readAttention(params: URLSearchParams): AttentionTarget | undefined {
  for (const page of Object.keys(ID_PARAM) as AttentionPage[]) {
    const id = params.get(ID_PARAM[page]);
    if (!id) continue;
    const raw = params.get('review');
    const review = REVIEW_FLOWS.find((flow) => flow === raw);
    return { page, id, ...(review ? { review } : {}) };
  }
  return undefined;
}

/** DOM id for a row, so a link can find it once the list has rendered. */
export function rowDomId(page: AttentionPage, id: string): string {
  return `${ID_PARAM[page]}-${id}`;
}

/** How long a revealed row stays tinted before fading back. */
const FLASH_MS = 2000;

/**
 * Scroll a row into view, focus it, and tint it briefly.
 *
 * Focus is the part that matters beyond looks: it puts keyboard and screen
 * reader users on the row too, and when the caller opens a review dialog right
 * after, `Modal` captures this row as the opener and hands focus back here on
 * close instead of falling through to the top of the page.
 */
export function revealRow(page: AttentionPage, id: string): void {
  const el = document.getElementById(rowDomId(page, id));
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.focus({ preventScroll: true });

  // A timed class rather than a CSS animation: the global
  // `prefers-reduced-motion` rule collapses every animation to 0.01ms, which
  // would erase the highlight entirely for the users who most need a static one.
  el.classList.add('row-flash');
  window.setTimeout(() => el.classList.remove('row-flash'), FLASH_MS);
}

/**
 * Consume an attention link on arrival, once.
 *
 * `ready` must be false until the page's rows are rendered — `onArrive`
 * reaches into the DOM for the row, and firing it against an empty list would
 * silently do nothing. The query is stripped as soon as it is handled so that a
 * refresh, or Back from a dialog, doesn't reopen a review already dealt with.
 */
export function useAttentionTarget(
  page: AttentionPage,
  ready: boolean,
  onArrive: (id: string, review: ReviewFlow | undefined) => void,
): void {
  const [params, setParams] = useSearchParams();

  // Callers pass an inline arrow; a ref keeps `onArrive` out of the dependency
  // list so a re-render can't re-fire the arrival.
  const onArriveRef = useRef(onArrive);
  onArriveRef.current = onArrive;
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || !ready) return;
    const target = readAttention(params);
    if (!target || target.page !== page) return;
    handled.current = true;
    setParams(new URLSearchParams(), { replace: true });
    onArriveRef.current(target.id, target.review);
  }, [ready, page, params, setParams]);
}
